import SelectField from '@/shared/components/form/Fields/SelectField';
import { FormGroupLabel } from '@/shared/components/form/FormElements';
import Select from '@/shared/components/form/Select';
import React from 'react';
import { Field, Form } from 'react-final-form';
import AllSelectField from '../form/Fields/AllSelectField';
import CheckBoxField from '../form/Fields/CheckBoxField';
import CheckBoxGroupField from '../form/Fields/CheckBoxGroupField';
import DateIntervalPickerField from '../form/Fields/DateIntervalPickerField';
import DatePickerField from '../form/Fields/DatePickerField';
import DateTimePickerField from '../form/Fields/DateTimePickerField';
import DropZoneField from '../form/Fields/DropZoneField';
import DropZoneMultipleField from '../form/Fields/DropZoneMultipleField';
import FileInputField from '../form/Fields/FileInputField';
import MultipleFilesInputField from '../form/Fields/MultipleFilesInputField';
import MultiSelectField from '../form/Fields/MultiSelectField';
import PasswordField from '../form/Fields/PasswordField';
import PostfixTextField from '../form/Fields/PostfixTextField';
import PrefixTextField from '../form/Fields/PrefixTextField';
import RadioButtonGroupField from '../form/Fields/RadioButtonGroupField';
import RichTextEditorField from '../form/Fields/RichTextEditorField';
import TextAreaField from '../form/Fields/TextAreaField';
import TextField from '../form/Fields/TextField';
import TimePickerField from '../form/Fields/TimePickerField';
const Input = React.memo(
	({
		name,
		label,
		select,
		children,
		maxLength = null,
		error,
		value,
		multiline = false,
		rowsMax,
		start,
		options,
		role,
		sites,
		intervalLabel,
		placeholder,
		initialValue,
		onChange,
		onBlur,
		multiple,
		type,
		disabled,
		readOnly,
		component: InputComponent,
		padding = 0,
		pattern,
		defaultValue,
		endAdornment,
		onClick,
		variant,
		datatestid,
		...rest
	}) => {
		return (
			<div style={{ width: '100%', padding }}>
				<Field
					data-testid={datatestid}
					start={start}
					name={name}
					label={label}
					role={role}
					select={!!select}
					multiple={multiple}
					onChange={onChange}
					initialValue={initialValue || defaultValue}
					maxLength={maxLength}
					error={error}
					options={options}
					multiline={!!multiline}
					rowsMax={rowsMax}
					sites={sites}
					disabled={disabled}
					type={type}
					placeholder={placeholder}
					readOnly={readOnly}
					pattern={pattern}
					variant={variant}
					endAdornment={endAdornment}
					onClick={onClick}
					{...rest}
				>
					{props => (
						<div>
							{intervalLabel && (
								<FormGroupLabel>{intervalLabel}</FormGroupLabel>
							)}
							<InputComponent {...props} error={error} data-testid={datatestid}>
								{children}
							</InputComponent>
						</div>
					)}
				</Field>
			</div>
		);
	},
);
Input.displayName = 'Input';
export default Input;

export const DefaultField = React.memo(props => {
	return <Field {...props}>{props.children}</Field>;
});
DefaultField.displayName = 'DefaultField';

export const DefaultForm = React.memo(props => {
	return <Form {...props}>{props.children}</Form>;
});
DefaultForm.displayName = 'DefaultForm';

export const DefaultSelect = React.memo(
	({
		name,
		options = [],
		placeholder = 'Select an option',
		isMulti = false,
		onChangeCallback,
		initialValue,
		...rest
	}) => {
		return (
			<DefaultField name={name} initialValue={initialValue}>
				{({ input, meta }) => {
					const error = (meta.touched || meta.dirty) && meta.error;

					const selectedOption = isMulti
						? options.filter(option => input.value?.includes(option.value)) ||
							null
						: options.find(option => option.value === input.value) || null;

					return (
						<Select
							{...input}
							value={selectedOption}
							options={options}
							placeholder={placeholder}
							isMulti={isMulti}
							error={error}
							onChange={selectedOption => {
								if (isMulti) {
									input.onChange(selectedOption?.map(option => option?.value));
								} else {
									input.onChange(selectedOption.value);
								}
								if (onChangeCallback) onChangeCallback(selectedOption);
							}}
							{...rest}
						/>
					);
				}}
			</DefaultField>
		);
	},
);

DefaultSelect.displayName = 'DefaultSelect';

export const FormSelectField = React.memo(
	({ name, label, placeholder, options, onChange, helperText, disabled }) => {
		return (
			<DefaultField
				name={name}
				label={label}
				options={options}
				placeholder={placeholder}
				helperText={helperText}
				disabled={disabled}
			>
				{({ input, meta, ...rest }) => {
					return (
						<SelectField
							{...input}
							{...meta}
							{...rest}
							defaultValue={meta.initial}
							error={(meta.touched || meta.dirty) && meta.error}
							onChange={selectedOption => {
								input.onChange(selectedOption);
								if (onChange) onChange(selectedOption);
							}}
						/>
					);
				}}
			</DefaultField>
		);
	},
);

FormSelectField.displayName = 'FormSelectField';

export const FormMultiSelectField = React.memo(
	({ name, label, placeholder, options, helperText }) => {
		return (
			<DefaultField
				name={name}
				label={label}
				options={options}
				placeholder={placeholder}
				helperText={helperText}
			>
				{({ input, meta, ...rest }) => {
					return (
						<MultiSelectField
							{...input}
							{...meta}
							{...rest}
							defaultValue={meta.initial}
							error={(meta.touched || meta.dirty) && meta.error}
						/>
					);
				}}
			</DefaultField>
		);
	},
);
FormMultiSelectField.displayName = 'FormMultiSelectField';

export const FormAllSelectField = React.memo(
	({ name, label, placeholder, options, helperText }) => {
		return (
			<DefaultField
				name={name}
				label={label}
				options={options}
				placeholder={placeholder}
				helperText={helperText}
			>
				{({ input, meta, ...rest }) => {
					return (
						<AllSelectField
							{...input}
							{...meta}
							{...rest}
							defaultValue={meta.initial}
							error={(meta.touched || meta.dirty) && meta.error}
						/>
					);
				}}
			</DefaultField>
		);
	},
);

FormAllSelectField.displayName = 'FormAllSelectField';

export const FormTextField = React.memo(
	({
		name,
		label,
		placeholder,
		variant,
		type,
		disabled,
		helperText,
		readOnly,
	}) => {
		return (
			<DefaultField
				name={name}
				label={label}
				placeholder={placeholder}
				variant={variant}
				type={type}
				disabled={disabled}
				helperText={helperText}
				readOnly={readOnly}
			>
				{({ input, meta, ...rest }) => {
					return (
						<TextField
							{...input}
							{...meta}
							{...rest}
							defaultValue={meta.initial}
							error={(meta.touched || meta.dirty) && meta.error}
						/>
					);
				}}
			</DefaultField>
		);
	},
);

FormTextField.displayName = 'FormTextField';

export const FormPrefixTextField = React.memo(
	({
		name,
		label,
		placeholder,
		variant,
		prefix,
		includePrefixInValue,
		helperText,
	}) => {
		return (
			<DefaultField
				name={name}
				label={label}
				prefix={prefix}
				includePrefixInValue={includePrefixInValue}
				placeholder={placeholder}
				variant={variant}
				helperText={helperText}
			>
				{({ input, meta, ...rest }) => {
					return (
						<PrefixTextField
							{...input}
							{...meta}
							{...rest}
							defaultValue={meta.initial}
							error={(meta.touched || meta.dirty) && meta.error}
						/>
					);
				}}
			</DefaultField>
		);
	},
);

FormPrefixTextField.displayName = 'FormPrefixTextField';

export const FormPostfixTextField = React.memo(
	({
		name,
		label,
		postfix,
		includePostfixInValue,
		placeholder,
		variant,
		helperText,
	}) => {
		return (
			<DefaultField
				name={name}
				label={label}
				postfix={postfix}
				includePostfixInValue={includePostfixInValue}
				placeholder={placeholder}
				variant={variant}
				helperText={helperText}
			>
				{({ input, meta, ...rest }) => {
					return (
						<PostfixTextField
							{...input}
							{...meta}
							{...rest}
							defaultValue={meta.initial}
							error={(meta.touched || meta.dirty) && meta.error}
						/>
					);
				}}
			</DefaultField>
		);
	},
);

FormPostfixTextField.displayName = 'FormPostfixTextField';

export const FormPasswordField = React.memo(
	({ name, label, placeholder, variant, helperText }) => {
		return (
			<DefaultField
				name={name}
				variant={variant}
				label={label}
				placeholder={placeholder}
				helperText={helperText}
			>
				{({ input, meta, ...rest }) => (
					<PasswordField
						{...input}
						{...meta}
						{...rest}
						defaultValue={meta.initial}
						error={meta?.touched && meta?.error}
					/>
				)}
			</DefaultField>
		);
	},
);

FormPasswordField.displayName = 'FormPasswordField';

export const FormCheckboxField = React.memo(
	({ name, label, helperText, disabled, marginBottom }) => {
		return (
			<DefaultField
				name={name}
				label={label}
				helperText={helperText}
				disabled={disabled}
			>
				{({ input, meta, ...rest }) => {
					return (
						<CheckBoxField
							{...input}
							{...meta}
							{...rest}
							checked={input.value}
							marginBottom={marginBottom}
							error={(meta.touched || meta.dirty) && meta.error}
						/>
					);
				}}
			</DefaultField>
		);
	},
);

FormCheckboxField.displayName = 'FormCheckboxField';

export const FormCheckBoxGroupField = React.memo(
	({ name, label, options, helperText, hideLabel }) => {
		return (
			<DefaultField
				name={name}
				label={label}
				options={options}
				helperText={helperText}
			>
				{({ input, meta, ...rest }) => {
					console.log({ meta, input, rest });
					return (
						<CheckBoxGroupField
							{...input}
							{...meta}
							{...rest}
							defaultValues={meta.initial}
							error={(meta.touched || meta.dirty) && meta.error}
							hideLabel={hideLabel}
						/>
					);
				}}
			</DefaultField>
		);
	},
);

FormCheckBoxGroupField.displayName = 'FormCheckBoxGroupField';

export const FormTextAreaField = React.memo(
	({ name, label, placeholder, helperText }) => {
		return (
			<DefaultField
				name={name}
				label={label}
				placeholder={placeholder}
				helperText={helperText}
			>
				{({ input, meta, ...rest }) => {
					console.log('form_text_area_field', { meta, input, rest });
					return (
						<TextAreaField
							{...input}
							{...meta}
							{...rest}
							defaultValue={meta.initial}
							error={(meta.touched || meta.dirty) && meta.error}
						/>
					);
				}}
			</DefaultField>
		);
	},
);

FormTextAreaField.displayName = 'FormTextAreaField';

export const FormFileInputField = React.memo(
	({ name, label, helperText, useIcon, accept }) => {
		return (
			<DefaultField
				name={name}
				label={label}
				helperText={helperText}
				useIcon={useIcon}
				accept={accept}
			>
				{({ input, meta, ...rest }) => {
					return (
						<FileInputField
							{...input}
							{...meta}
							{...rest}
							error={(meta.touched || meta.dirty) && meta?.error}
						/>
					);
				}}
			</DefaultField>
		);
	},
);

FormFileInputField.displayName = 'FormFileInputField';

export const FormMultipleFilesInputField = React.memo(
	({ name, label, helperText, useIcon }) => {
		return (
			<DefaultField
				name={name}
				label={label}
				helperText={helperText}
				useIcon={useIcon}
			>
				{({ input, meta, ...rest }) => {
					return (
						<MultipleFilesInputField
							{...input}
							{...meta}
							{...rest}
							error={(meta.touched || meta.dirty) && meta?.error}
						/>
					);
				}}
			</DefaultField>
		);
	},
);

FormMultipleFilesInputField.displayName = 'FormMultipleFilesInputField';

export const FormDropZoneField = React.memo(({ name, label, helperText }) => {
	return (
		<DefaultField name={name} label={label} helperText={helperText}>
			{({ input, meta, ...rest }) => {
				return (
					<DropZoneField
						{...input}
						{...meta}
						{...rest}
						error={(meta.touched || meta.dirty) && meta?.error}
					/>
				);
			}}
		</DefaultField>
	);
});

FormDropZoneField.displayName = 'FormDropZoneField';

export const FormDropZoneMultipleField = React.memo(
	({ name, label, helperText }) => {
		return (
			<DefaultField name={name} label={label} helperText={helperText}>
				{({ input, meta, ...rest }) => {
					return (
						<DropZoneMultipleField
							{...input}
							{...meta}
							{...rest}
							error={(meta.touched || meta.dirty) && meta?.error}
						/>
					);
				}}
			</DefaultField>
		);
	},
);

FormDropZoneMultipleField.displayName = 'FormDropZoneMultipleField';

export const FormRadioButtonGroupField = React.memo(
	({
		name,
		label,
		disabled,
		styleType,
		radioButtons,
		alignVertically,
		helperText,
		hideLabel,
	}) => (
		<DefaultField
			name={name}
			label={label}
			disabled={disabled}
			styleType={styleType}
			radioButtons={radioButtons}
			helperText={helperText}
		>
			{({ input, meta, ...rest }) => {
				return (
					<RadioButtonGroupField
						{...input}
						{...meta}
						{...rest}
						error={(meta.touched || meta.dirty) && meta?.error}
						alignVertically={alignVertically}
						hideLabel={hideLabel}
					/>
				);
			}}
		</DefaultField>
	),
);

FormRadioButtonGroupField.displayName = 'FormRadioButtonGroupField';

export const FormRichTextEditorField = React.memo(
	({ name, label, initialContent, helperText, limited }) => (
		<DefaultField
			name={name}
			label={label}
			initialValue={initialContent}
			helperText={helperText}
			limited={limited}
		>
			{({ input, meta, ...rest }) => {
				return (
					<RichTextEditorField
						{...input}
						{...meta}
						{...rest}
						initialContent={meta.initial}
						error={(meta.touched || meta.dirty) && meta?.error}
					/>
				);
			}}
		</DefaultField>
	),
);

FormRichTextEditorField.displayName = 'FormRichTextEditorField';

export const FormDatePickerField = React.memo(
	({ name, label, defaultValue, dateOutputFormat, helperText, minDate }) => (
		<DefaultField
			name={name}
			label={label}
			initialValue={defaultValue}
			dateOutputFormat={dateOutputFormat}
			helperText={helperText}
			minDate={minDate}
		>
			{({ input, meta, ...rest }) => {
				return (
					<DatePickerField
						{...input}
						{...meta}
						{...rest}
						defaultValue={meta?.initial}
						error={(meta.touched || meta.dirty) && meta?.error}
					/>
				);
			}}
		</DefaultField>
	),
);

FormDatePickerField.displayName = 'FormDatePickerField';

export const FormDateIntervalPickerField = React.memo(
	({ name, label, dateOutputFormat, helperText }) => {
		return (
			<DefaultField
				name={name}
				label={label}
				dateOutputFormat={dateOutputFormat}
				helperText={helperText}
			>
				{({ input, meta, ...rest }) => {
					return (
						<DateIntervalPickerField
							{...input}
							{...meta}
							{...rest}
							defaultRange={meta?.initial}
							error={(meta.touched || meta.dirty) && meta?.error}
						/>
					);
				}}
			</DefaultField>
		);
	},
);

FormDateIntervalPickerField.displayName = 'FormDateIntervalPickerField';

export const FormDateTimePickerField = React.memo(
	({ name, label, defaultValue, dateOutputFormat, helperText }) => {
		return (
			<DefaultField
				name={name}
				label={label}
				initialValue={defaultValue}
				dateOutputFormat={dateOutputFormat}
				helperText={helperText}
			>
				{({ input, meta, ...rest }) => {
					return (
						<DateTimePickerField
							{...input}
							{...meta}
							{...rest}
							defaultValue={meta?.initial}
							error={(meta.touched || meta.dirty) && meta?.error}
						/>
					);
				}}
			</DefaultField>
		);
	},
);

FormDateTimePickerField.displayName = 'FormDateTimePickerField';

export const FormTimePickerField = React.memo(
	({ name, label, defaultValue }) => {
		return (
			<DefaultField name={name} label={label} initialValue={defaultValue}>
				{({ input, meta, ...rest }) => {
					return (
						<TimePickerField
							{...input}
							{...meta}
							{...rest}
							defaultValue={meta?.initial}
							error={(meta.touched || meta.dirty) && meta?.error}
						/>
					);
				}}
			</DefaultField>
		);
	},
);

FormTimePickerField.displayName = 'FormTimePickerField';
